import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Navigation,
  NavigationItem,
  IconButton,
  Drawer,
} from '@americanexpress/dls-react';
import { IconMenu } from '@americanexpress/dls-icons';
import { getIcon } from './Icons';

// NavigationItem cloneElement adds `filled` to the icon element it receives.
// Wrapping the DLS icon in a span makes NavIcon the clone target — span ignores
// the `filled` prop — so our explicit `shouldFill` value is always used for the inner icon.
const NavIcon = ({
  iconName, shouldFill, size, theme,
}) => (
  <span style={{ display: 'inline-flex' }}>
    {getIcon(iconName, { isFilled: shouldFill, size, theme })}
  </span>
);

NavIcon.propTypes = {
  iconName: PropTypes.string.isRequired,
  shouldFill: PropTypes.bool.isRequired,
  size: PropTypes.string,
  theme: PropTypes.string,
};

const Nav = (props) => {
  const {
    cssProperties,
    setFormSubmissionPayload,
    showInContainer = false,
    childElements,
    defaultOpenIds = [],
    elementContainer = [],
    handleContinue,
  } = props;

  const navItemRefs = useRef({});
  const [IsSmallScreenDrawerOpen, setIsSmallScreenDrawerOpen] = useState(false);

  // The page the user is currently viewing (source of truth from container state).
  const currentPageFromContainer = elementContainer.find(
    (container) => container?.isPageVisited && container?.page != null
  )?.page;
  const normalizedContainerPage = Number(currentPageFromContainer);
  const currentViewedPage = Number.isFinite(normalizedContainerPage)
    ? normalizedContainerPage
    : null;

  // The highest page number the user has ever reached (controls what is accessible).
  // `isPageVisited` is only the current page; `isFieldsVisited` marks pages completed earlier.
  // Pages marked with `excludeFromNavProgress: true` in JSON are excluded so they
  // cannot inflate this value and incorrectly mark all nav items as visited.
  const maxVisitedPage = elementContainer.reduce((max, container) => {
    if ((container.isPageVisited || container.isFieldsVisited) && container.page != null
      && !container.excludeFromNavProgress) {
      return Math.max(max, Number(container.page));
    }
    return max;
  }, null);

  const handleNavClick = (pageNo) => {
    if (maxVisitedPage !== null && pageNo <= maxVisitedPage && pageNo !== currentViewedPage) {
      const isForward = currentViewedPage !== null && pageNo > currentViewedPage;
      if (handleContinue && isForward) {
        const form = document.querySelector('form');
        if (form && !form.reportValidity()) {
          setFormSubmissionPayload((prev) => ({
            ...prev,
            navErrorPages: [...prev.navErrorPages || [], currentViewedPage],
          }));
          return;
        }
        setFormSubmissionPayload((prev) => ({
          ...prev,
          returnToSummaryAfterEdit: false,
          navErrorPages: (prev.navErrorPages || []).filter((p) => p !== currentViewedPage),
        }));
        handleContinue(pageNo);
      } else if (handleContinue) {
        setFormSubmissionPayload((prev) => ({
          ...prev,
          returnToSummaryAfterEdit: false,
        }));
        handleContinue(pageNo);
      } else {
        setFormSubmissionPayload((prev) => ({
          ...prev,
          navDefaultSection: pageNo,
          elemId: null,
        }));
      }
    }
  };

  // eslint-disable-next-line max-params -- handler needs event, pageNo, isFuture, and isCurrentSection
  const handleNavItemClick = (event, pageNo, isFuture, isCurrentSection) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isFuture && !isCurrentSection) {
      handleNavClick(pageNo);
      setIsSmallScreenDrawerOpen(false);
    }
  };

  useEffect(() => {
    let frameId;
    const outerFrameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(() => {
        defaultOpenIds.forEach((nodeId) => {
          const el = navItemRefs.current[nodeId];
          if (el) {
            const toggleTarget = el.querySelector('button') || el.firstElementChild;
            if (toggleTarget) toggleTarget.click();
          }
        });
      });
    });
    return () => {
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(frameId);
    };
  }, [defaultOpenIds]);

  // eslint-disable-next-line no-unused-vars -- isRoot is kept for potential future use
  const renderNavItems = (items, isRoot = false) => {
    if (!items || items.length === 0) return null;

    const nodes = items.map((item) => {
      const navId = `${item.pageNo}_${item.label}`;
      const itemPageNo = Number(item.pageNo);
      // isFuture: page never visited yet — not clickable
      const isFuture = maxVisitedPage === null || itemPageNo > maxVisitedPage;
      // isCurrentSection: the page the user is currently viewing
      const isCurrentSection = currentViewedPage !== null && itemPageNo === currentViewedPage;
      // isPast: visited but not the current view (filled icon, accessible)
      const isPast = !isCurrentSection && !isFuture;
      const isActive = isCurrentSection;
      const completedIconName = item.iconForCompleted || item.completedIcon || item.icon;
      const incompleteIconName = item.iconForIncomplete || item.incompleteIcon || item.icon;
      const resolvedIconName = isPast ? completedIconName : incompleteIconName;
      const completedIconFilled = item.completedIconFilled !== undefined
        ? item.completedIconFilled
        : true;
      const shouldFillIcon = isPast && completedIconFilled;

      const itemIcon = resolvedIconName
        ? <NavIcon iconName={resolvedIconName} size="md" shouldFill={shouldFillIcon} />
        : null;
      const nestedItems = item.childElements?.length > 0
        ? item.childElements
        : null;

      return (
        <div
          key={navId}
          ref={(el) => { if (item.nodeId) navItemRefs.current[item.nodeId] = el; }}
        >
          <NavigationItem
            id={navId}
            label={item.label}
            icon={itemIcon}
            isActive={isActive}
            aria-current={isCurrentSection ? 'page' : undefined}
            onClick={(event) => handleNavItemClick(event, item.pageNo, isFuture, isCurrentSection)}
            disabled={isFuture}
            aria-disabled={isFuture}
            style={isFuture ? { cursor: 'not-allowed' } : undefined}
          >
            {nestedItems ? renderNavItems(nestedItems, false) : null}
          </NavigationItem>
        </div>
      );
    });

    return nodes.length > 1 ? nodes : nodes[0];
  };

  return (
    <div className={cssProperties?.style}>
      <div className="hidden-lg-up">
        <IconButton onClick={() => setIsSmallScreenDrawerOpen(true)} screenReaderLabel="Ouvrir le menu">
          <IconMenu />
        </IconButton>
        <Drawer
          isOpen={IsSmallScreenDrawerOpen}
          onClose={() => setIsSmallScreenDrawerOpen(false)}
        >
          <Navigation showInContainer={showInContainer}>
            {renderNavItems(childElements, true)}
          </Navigation>
        </Drawer>
      </div>
      <div className="hidden-md-down">
        <Navigation showInContainer={showInContainer}>
          {renderNavItems(childElements, true)}
        </Navigation>
      </div>
    </div>
  );
};

Nav.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.string),
  childElements: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      pageNo: PropTypes.number,
      icon: PropTypes.string,
      iconForCompleted: PropTypes.string,
      completedIcon: PropTypes.string,
      iconForIncomplete: PropTypes.string,
      incompleteIcon: PropTypes.string,
      completedIconFilled: PropTypes.bool,
      childElements: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  showInContainer: PropTypes.bool,
  defaultOpenIds: PropTypes.arrayOf(PropTypes.string),
  elementContainer: PropTypes.arrayOf(PropTypes.shape({
    page: PropTypes.number,
    isPageVisited: PropTypes.bool,
    isFieldsVisited: PropTypes.bool,
  })),
  handleContinue: PropTypes.func,
  setFormSubmissionPayload: PropTypes.func,
};

export default Nav;
